import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, X, Share2, Star, Trash2, LayoutGrid, ChevronDown } from "lucide-react";

const TONE_COLOR = { good: "#00F5D4", warning: "#f59e0b", critical: "#f43f5e", neutral: "#9CA3AF" };

function fmtValue(w) {
  if (w.value == null) return "—";
  if (w.unit === "currency") {
    try { return new Intl.NumberFormat(undefined, { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(w.value); }
    catch { return `£${w.value}`; }
  }
  if (w.unit === "h") return `${w.value}h`;
  return w.value;
}

function WidgetCard({ widget, canEdit, onRemove }) {
  const nav = useNavigate();
  const spanClass = (widget.w || 1) >= 2 ? "sm:col-span-2" : "";
  return (
    <div className={`group relative bg-[#0d1117] border border-[#21262d] rounded-lg p-4 ${spanClass} ${widget.link ? "cursor-pointer hover:border-[#00F5D4]/30" : ""} transition-colors`}
      onClick={() => widget.link && nav(widget.link)}>
      {canEdit && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(widget.widget_id); }}
          className="absolute top-2 right-2 text-[#484f58] hover:text-rose-400 opacity-0 group-hover:opacity-100">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      <div className="text-xs text-[#7d8590] mb-2">{widget.label}</div>

      {widget.kind === "kpi" && (
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold" style={{ color: TONE_COLOR[widget.tone] || "#fff" }}>{fmtValue(widget)}</span>
        </div>
      )}

      {widget.kind === "list" && (
        (widget.items || []).length === 0 ? (
          <div className="text-sm text-[#484f58]">Nothing here.</div>
        ) : (
          <div className="space-y-2">
            {widget.items.map((it, i) => (
              <div key={i} className="text-sm">
                <div className="text-white truncate">{it.primary}</div>
                {it.secondary && <div className="text-[11px] text-[#7d8590]">{it.secondary}</div>}
              </div>
            ))}
          </div>
        )
      )}

      {widget.kind === "chart" && (
        (widget.data || []).length === 0 ? (
          <div className="text-sm text-[#484f58]">No data this week.</div>
        ) : (
          <div className="space-y-1.5">
            {(() => {
              const max = Math.max(...widget.data.map(d => d.value), 1);
              return widget.data.map((d) => (
                <div key={d.name} className="text-xs">
                  <div className="flex justify-between text-[#9CA3AF] mb-0.5"><span className="truncate">{d.name}</span><span className="font-mono">{d.value}</span></div>
                  <div className="w-full h-1.5 bg-[#1c2128] rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-[#00F5D4]" style={{ width: `${(d.value / max) * 100}%` }} />
                  </div>
                </div>
              ));
            })()}
          </div>
        )
      )}
    </div>
  );
}

export default function Dashboards() {
  const [dashboards, setDashboards] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", is_shared: false });
  const [showAddWidget, setShowAddWidget] = useState(false);
  const [saving, setSaving] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);

  // Returns the fresh list so callers can pick a sensible active dashboard
  // without depending on possibly-stale `activeId` state inside this closure.
  const loadList = useCallback(async () => {
    try {
      const [dRes, cRes] = await Promise.all([api.get("/dashboards"), api.get("/dashboards/widget-catalog")]);
      setDashboards(dRes.data || []);
      setCatalog(cRes.data || []);
      return dRes.data || [];
    } catch {
      toast.error("Failed to load dashboards");
      return [];
    }
  }, []);

  const loadData = useCallback(async (id) => {
    if (!id) { setData(null); return; }
    try {
      const { data } = await api.get(`/dashboards/${id}/data`);
      setData(data);
    } catch {
      toast.error("Failed to load dashboard data");
    }
  }, []);

  // Initial load: fetch the list once, then land on the user's default dashboard.
  useEffect(() => {
    (async () => {
      const list = await loadList();
      if (list.length) {
        const def = await api.get("/dashboards/default").catch(() => ({ data: null }));
        setActiveId(def.data?.dashboard_id || list[0].dashboard_id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { loadData(activeId); }, [activeId, loadData]);

  const active = dashboards.find(d => d.dashboard_id === activeId);

  const createDashboard = async () => {
    if (!newForm.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const { data } = await api.post("/dashboards", newForm);
      toast.success("Dashboard created");
      setShowNew(false);
      setNewForm({ name: "", is_shared: false });
      await loadList();
      setActiveId(data.dashboard_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create dashboard");
    } finally {
      setSaving(false);
    }
  };

  const toggleShare = async () => {
    try {
      await api.put(`/dashboards/${activeId}`, { is_shared: !active.is_shared });
      toast.success(active.is_shared ? "No longer shared" : "Shared with the team");
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const makeDefault = async () => {
    try {
      await api.put(`/dashboards/${activeId}`, { is_default: true });
      toast.success("Set as your default dashboard");
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const deleteDashboard = async () => {
    if (!window.confirm(`Delete dashboard "${active.name}"?`)) return;
    try {
      await api.delete(`/dashboards/${activeId}`);
      toast.success("Dashboard deleted");
      const list = await loadList();
      setActiveId(list.length ? list[0].dashboard_id : null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete dashboard");
    }
  };

  const addWidget = async (widgetType) => {
    const meta = catalog.find(w => w.widget_type === widgetType);
    try {
      await api.post(`/dashboards/${activeId}/widgets`, {
        widget_type: widgetType, w: meta?.default_w || 1, h: meta?.default_h || 1,
      });
      setShowAddWidget(false);
      loadData(activeId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add widget");
    }
  };

  const removeWidget = async (widgetId) => {
    try {
      await api.delete(`/dashboards/${activeId}/widgets/${widgetId}`);
      loadData(activeId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove widget");
    }
  };

  const usedTypes = new Set((data?.widgets || []).map(w => w.widget_type));
  const availableToAdd = catalog.filter(c => !usedTypes.has(c.widget_type));

  return (
    <DarkPage><div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><LayoutGrid className="w-5 h-5 text-[#7d8590]" /> Dashboards</h1>
          {dashboards.length > 0 && (
            <div className="relative">
              <button onClick={() => setSwitcherOpen(v => !v)} className="flex items-center gap-1.5 text-sm text-[#9CA3AF] hover:text-white bg-[#161b22] border border-[#30363d] rounded-md px-3 py-1.5">
                {active?.name || "Select…"} {active?.is_shared && <Share2 className="w-3 h-3 text-[#00F5D4]" />} <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {switcherOpen && (
                <div className="absolute z-10 mt-1 bg-[#0d1117] border border-[#21262d] rounded-md w-64 py-1 shadow-xl">
                  {dashboards.map(d => (
                    <button key={d.dashboard_id} onClick={() => { setActiveId(d.dashboard_id); setSwitcherOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[#161b22] ${d.dashboard_id === activeId ? "text-[#00F5D4]" : "text-white"}`}>
                      <span className="truncate">{d.name}</span>
                      <span className="flex items-center gap-1 text-[#7d8590]">
                        {d.is_default && <Star className="w-3 h-3 fill-current text-amber-400" />}
                        {d.is_shared && <Share2 className="w-3 h-3" />}
                        {!d.is_mine && <span className="text-[10px]">{d.owner_name}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {active && (
            <>
              {active.can_edit && (
                <Button onClick={() => setShowAddWidget(true)} variant="outline" className="border-[#30363d] text-[#7d8590] hover:text-white">
                  <Plus className="w-4 h-4 mr-1" /> Add widget
                </Button>
              )}
              {active.is_mine && !active.is_default && (
                <button onClick={makeDefault} className="text-xs px-3 py-1.5 rounded-full border border-[#30363d] text-[#7d8590] hover:text-white flex items-center gap-1">
                  <Star className="w-3 h-3" /> Set default
                </button>
              )}
              {active.can_edit && (
                <button onClick={toggleShare} className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1 ${active.is_shared ? "border-[#00F5D4]/30 text-[#00F5D4] bg-[#00F5D4]/10" : "border-[#30363d] text-[#7d8590] hover:text-white"}`}>
                  <Share2 className="w-3 h-3" /> {active.is_shared ? "Shared" : "Share"}
                </button>
              )}
              {active.can_edit && (
                <button onClick={deleteDashboard} className="text-xs px-3 py-1.5 rounded-full border border-rose-500/20 text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 flex items-center gap-1">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </>
          )}
          <Button onClick={() => { setNewForm({ name: "", is_shared: false }); setShowNew(true); }} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> New dashboard
          </Button>
        </div>
      </div>

      {dashboards.length === 0 ? (
        <div className="text-center py-20 text-[#7d8590] text-sm">
          No dashboards yet. Create one and add the widgets that matter to you — share it with the team if it's useful to everyone.
        </div>
      ) : !data ? (
        <div className="text-center py-20 text-[#7d8590] text-sm">Loading…</div>
      ) : data.widgets.length === 0 ? (
        <div className="text-center py-20 text-[#7d8590] text-sm">
          This dashboard is empty. {active?.can_edit && <button onClick={() => setShowAddWidget(true)} className="text-[#00F5D4] hover:underline">Add a widget</button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.widgets.map(w => (
            <WidgetCard key={w.widget_id} widget={w} canEdit={active?.can_edit} onRemove={removeWidget} />
          ))}
        </div>
      )}

      {/* New dashboard modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New dashboard</h2>
              <button onClick={() => setShowNew(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Name *</label>
                <input value={newForm.name} onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Delivery overview"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <label className="flex items-center gap-2 text-xs text-[#7d8590]">
                <input type="checkbox" checked={newForm.is_shared} onChange={e => setNewForm(f => ({ ...f, is_shared: e.target.checked }))} />
                Share with the whole team (read-only for everyone but you)
              </label>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={createDashboard} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">Create</Button>
              <Button onClick={() => setShowNew(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Add widget modal */}
      {showAddWidget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">Add a widget</h2>
              <button onClick={() => setShowAddWidget(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {availableToAdd.length === 0 ? (
              <div className="text-sm text-[#7d8590]">Every available widget is already on this dashboard.</div>
            ) : (
              <div className="space-y-1">
                {availableToAdd.map(w => (
                  <button key={w.widget_type} onClick={() => addWidget(w.widget_type)}
                    className="w-full text-left px-3 py-2.5 text-sm text-white hover:bg-[#161b22] rounded-md flex items-center justify-between">
                    {w.label}
                    <Plus className="w-3.5 h-3.5 text-[#7d8590]" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
